import type {
    ImageProvider,
    ImageProviderConfig,
    ImageResult,
    ImageSearchParams,
    UnsplashPhoto,
    UnsplashSearchResponse,
} from '../../types';

const IMAGE_EXTENSION = 'webp';
const UNSPLASH_API_BASE = 'https://api.unsplash.com';

function mapPhotoToModel(photo: UnsplashPhoto): ImageResult {
    return {
        id: photo.id,
        urls: { ...photo.urls, custom_large: photo.urls.raw + '&w=2400' + `&fm=${IMAGE_EXTENSION}` },
        width: photo.width,
        height: photo.height,
        description: photo?.description || '',
        authorName: photo.user.name,
        authorUrl: photo.user.links.html,
        links: photo.links,
        blurHash: photo.blur_hash,
        extension: IMAGE_EXTENSION,
    };
}

export class UnsplashProvider implements ImageProvider {
    private readonly baseUrl: string;
    private readonly headers: HeadersInit;

    constructor(config: ImageProviderConfig) {
        if (config.apiUrl) {
            // BFF proxy - auth handled by proxy
            this.baseUrl = config.apiUrl;
            this.headers = {};
        } else {
            // Direct API call with access key
            this.baseUrl = UNSPLASH_API_BASE;
            this.headers = {
                Authorization: `Client-ID ${config.accessKey}`,
            };
        }
    }

    private buildUrl(endpoint: string, params?: Record<string, string | number>): string {
        // Append endpoint to baseUrl (handles both direct API and BFF proxy)
        // e.g., "https://api.unsplash.com" + "/photos" or
        //       "https://stage.sendpulse.test/websites/builder/api/unsplash" + "/photos"
        const baseWithoutTrailingSlash = this.baseUrl.replace(/\/$/, '');
        const endpointWithLeadingSlash = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        let url = `${baseWithoutTrailingSlash}${endpointWithLeadingSlash}`;

        if (params && Object.keys(params).length > 0) {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                searchParams.set(key, String(value));
            });
            url += `?${searchParams.toString()}`;
        }

        return url;
    }

    private async fetchJson<T>(endpoint: string, params?: Record<string, string | number>): Promise<T | null> {
        const url = this.buildUrl(endpoint, params);

        try {
            const response = await fetch(url, { headers: this.headers });

            if (!response.ok) {
                console.error(`Unsplash API error: ${response.status} ${response.statusText}`);
                return null;
            }

            const ressponseData = (await response.json()) as { data: T };
            return ressponseData?.data;
        } catch (error) {
            console.error('Unsplash API fetch error:', error);
            return null;
        }
    }

    async search(params: ImageSearchParams): Promise<ImageResult[] | null> {
        const response = await this.fetchJson<UnsplashSearchResponse>('/search/photos', {
            query: params.query,
            page: params.page ?? 1,
            per_page: params.perPage ?? 6,
        });

        if (!response) {
            return null;
        }

        return response.results.map(mapPhotoToModel);
    }

    async list(params?: { page?: number; perPage?: number }): Promise<ImageResult[] | null> {
        const response = await this.fetchJson<UnsplashPhoto[]>('/photos', {
            page: params?.page ?? 1,
            per_page: params?.perPage ?? 6,
        });

        if (!response || !Array.isArray(response)) {
            return null;
        }

        return response.map(mapPhotoToModel);
    }

    async trackDownload(image: ImageResult): Promise<void> {
        const downloadLocation = image.links.download_location;

        try {
            if (!this.headers['Authorization' as keyof HeadersInit]) {
                const url = new URL(downloadLocation);

                const proxyUrl = this.buildUrl(url.pathname, Object.fromEntries(url.searchParams));
                await fetch(proxyUrl, { headers: this.headers });
            } else {
                // Direct API call
                await fetch(downloadLocation, { headers: this.headers });
            }
        } catch (error) {
            console.error('Unsplash trackDownload error:', error);
        }
    }

    async getImageById(id: string): Promise<ImageResult | null> {
        const response = await this.fetchJson<UnsplashPhoto>(`/photos/${id}`);

        if (!response) {
            return null;
        }

        return mapPhotoToModel(response);
    }
}
